import React, { useRef } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';

import Segmented, { SegmentedOption } from 'components/DesignSystem/Segmented';
import { isDarkReadyPath, ThemePreference } from 'constants/theme';
import { applyThemeWithTransition, effectiveTheme, resolveTheme } from 'helper/theme';
import {
  selectResolvedTheme, selectSystemPrefersDark, selectThemePreference, setThemePreference,
} from 'redux/reducers/themeReducer';

import './index.scss';

const LABELS: Record<ThemePreference, string> = {
  light: 'Claro',
  dark: 'Escuro',
  system: 'Sistema',
};

const ICONS = { light: faSun, dark: faMoon, system: faDesktop };

const ORDER: ThemePreference[] = ['light', 'dark', 'system'];

export const PENDING_ROUTE_HINT = 'Esta tela ainda não tem modo escuro — ela continua no claro por enquanto.';

/**
 * Seletor de aparência (Claro · Escuro · Sistema).
 * `compact`: só ícones, para o header do desktop. `labeled`: ícone + texto, para a folha
 * "Mais" do mobile. A troca anima a partir do ponto clicado (helper/theme.ts).
 */
export default function ThemeSwitcher({ variant = 'compact' }: { variant?: 'compact' | 'labeled' }) {
  const dispatch = useDispatch();
  const preference = useSelector(selectThemePreference);
  const resolved = useSelector(selectResolvedTheme);
  const systemDark = useSelector(selectSystemPrefersDark);
  const { pathname } = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  const routePending = resolved === 'dark' && !isDarkReadyPath(pathname);
  const compact = variant === 'compact';

  const options: SegmentedOption<ThemePreference>[] = ORDER.map((value) => ({
    value,
    ariaLabel: compact ? LABELS[value] : undefined,
    iconNode: <FontAwesomeIcon icon={ICONS[value]} className="theme-switcher-icon" aria-hidden />,
    label: compact ? undefined : LABELS[value],
  }));

  const onChange = (next: ThemePreference) => {
    // Clique: o círculo nasce do ponteiro. Teclado: do centro do seletor.
    const rect = wrapperRef.current?.getBoundingClientRect();
    const origin = pointerRef.current
      ?? (rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : undefined);
    pointerRef.current = null;
    const nextTheme = effectiveTheme(resolveTheme(next, systemDark), pathname);
    applyThemeWithTransition(nextTheme, origin, () => {
      flushSync(() => { dispatch(setThemePreference(next)); });
    });
  };

  return (
    <div
      ref={wrapperRef}
      className={`theme-switcher theme-switcher-${variant}${routePending ? ' is-route-pending' : ''}`}
      title={compact ? `Aparência: ${LABELS[preference]}${routePending ? `. ${PENDING_ROUTE_HINT}` : ''}` : undefined}
      onPointerDown={(event) => { pointerRef.current = { x: event.clientX, y: event.clientY }; }}
    >
      <Segmented
        options={options}
        value={preference}
        onChange={onChange}
        fill={!compact}
        size="sm"
        ariaLabel="Aparência"
      />
      {!compact && routePending && <p className="theme-switcher-hint">{PENDING_ROUTE_HINT}</p>}
    </div>
  );
}
